import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

import type { SftpConnectionResponse, SftpSettingsBody, SftpSettingsResponse } from '../lib/api-client';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * What the caller needs to connect (`GET /sftp/connection`): host, port, their username,
 * the server's host-key fingerprint, the server's heartbeat status and whether their role
 * and keys permit a login.
 */
export function useSftpConnection({ enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [connection, setConnection] = useState<SftpConnectionResponse | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(undefined);
    try {
      setConnection(await apiClient.getSftpConnection());
      setStatus('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load SFTP connection details');
      setStatus('error');
    }
  }, [apiClient]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  return { connection, status, error, reload };
}

/**
 * The runtime SFTP settings (`/sftp/settings`: enable switch + source-IP allowlist),
 * stored on the platform root tenant and editable by any full_access caller. The
 * server 403s everyone else; `forbidden` lets the view hide the tab rather than show
 * an error (the api-keys hook's fallback idiom — no capability plumbing here).
 */
export function useSftpSettings({ enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [settings, setSettings] = useState<SftpSettingsResponse | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();
  const [forbidden, setForbidden] = useState(false);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(undefined);
    try {
      setSettings(await apiClient.getSftpSettings());
      setForbidden(false);
      setStatus('success');
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setSettings(null);
        setStatus('success');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load SFTP settings');
      setStatus('error');
    }
  }, [apiClient]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const save = useCallback(
    async (body: SftpSettingsBody): Promise<SftpSettingsResponse> => {
      const next = await apiClient.putSftpSettings(body);
      setSettings(next);
      return next;
    },
    [apiClient],
  );

  return { settings, status, error, forbidden, reload, save };
}
